<template>
  <v-container class="masterDetail">
    <v-row>
      <v-col cols="12" md="6" class="pageTtl">
        <h2>入力項目</h2>
        <div class="facility">（{{ selectedFacility?.name }}）</div>
      </v-col>
      <v-col cols="12" md="6" class="text-right d-flex justify-end">
        <v-card-actions class="px-0 py-0">
          <v-btn depressed small outlined @click="saveOrder">
            順番を保存
          </v-btn>
          <v-btn
            depressed
            small
            :disabled="!selectedUnitId"
            @click="dialog.show = true"
          >
            <v-icon class="white--text">mdi-plus</v-icon>
            追加
          </v-btn>
        </v-card-actions>
      </v-col>

      <v-col cols="12">
        <v-card tile>
          <v-container class="">
            <v-row>
              <v-col cols="12">
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="selectedUnitId"
                      :items="units"
                      outlined
                      dense
                      hide-details="auto"
                      label="区画種類または備品"
                      clearable
                      item-text="name"
                      item-value="id"
                      @change="load"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
          <custom-data-table
            :headers="table.headers"
            :items="table.items"
            item-class="pointer"
            :use-draggable="true"
            @click:row="handleClickRow"
            @updated-items="
              (v) => {
                table.items = v;
              }
            "
          >
          </custom-data-table>
          <field-add
            v-model="dialog"
            :selected="table.items"
            @add="add"
            @select="select"
          ></field-add>
          <field-detail
            v-model="detailDialog"
            @save="save"
            @destroy="remove"
          ></field-detail>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import { Field, Unit } from '@api/models';
import {
  GetAllFieldsRequest,
  GetAllFieldsResponse,
  SaveFieldRequest,
  UpdateFieldRequest,
  UpdateFieldResponse,
} from '@api-i/routes/field/field';
import {
  InsertUnitFieldRequest,
  InsertUnitFieldResponse,
  DeleteUnitFieldRequest,
  DeleteUnitFieldResponse,
  OrderUnitFieldRequest,
  OrderUnitFieldResponse,
} from '@api-i/routes/unitField/unitField';
import FieldAdd from './FieldAdd.vue';
import FieldDetail, { initFieldDetailItem } from './FieldDetail.vue';
import { handleApiError, handleUnknownError } from '@web/modules/error-handler';
import { loadUnits } from '@web/modules/master-loader';
import { mapActions } from '@web/store/snackbar';

export default Vue.extend({
  name: 'MastersField',

  pageOptions: {
    routeConfig: {
      path: '/masters/field',
    },
    props: {
      title: 'マスタ設定 - フィールド',
    },
  },

  components: {
    FieldAdd,
    FieldDetail,
  },

  data: () => ({
    selectedUnitId: '',
    units: [] as Unit[],
    table: {
      headers: [
        { text: '名称', value: 'name', cellClass: 'pointer' },
        { text: '対象', value: 'property', cellClass: 'pointer' },
        { text: '入力タイプ', value: 'typeName', cellClass: 'pointer' },
        { text: '入力サイト', value: 'targetSite', cellClass: 'pointer' },
      ],
      items: [] as Field[],
    },
    dialog: {
      show: false,
      item: {} as Partial<SaveFieldRequest>,
    },
    detailDialog: {
      show: false,
      item: {} as Partial<UpdateFieldRequest>,
      newMode: false,
    },
  }),

  computed: {
    selectedFacility() {
      return this.$store.getters['master/selectedFacility'];
    },
  },

  async mounted() {
    await this.loadUnits();
  },

  methods: {
    async loadUnits() {
      if (!this.selectedFacility) {
        return this.snackbarRegister({
          type: 'error',
          message:
            '施設が選択されていません。前の画面に戻り施設を選択して下さい。',
        });
      }
      const facilityId = this.selectedFacility.id;
      const units = await loadUnits(this, {
        facilityId: facilityId,
      });
      if (units) {
        this.units = units;
      }
    },

    async load() {
      if (!this.selectedUnitId) {
        this.table.items = [];
        return;
      }
      try {
        const result = await this.$api<
          GetAllFieldsResponse,
          GetAllFieldsRequest
        >({
          path: '/field',
          method: 'get',
          params: {
            scope: 'units',
            unitId: this.selectedUnitId,
          },
        });

        this.table.items = result.fields;
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: [
              'データの取得に失敗しました。下記内容を確認してください。',
            ],
          })
        ) {
          handleUnknownError(error, this);
        }
        throw error;
      }
    },

    async add() {
      if (!this.selectedUnitId) {
        return;
      }
      try {
        const result = await this.$api({
          path: '/field/add',
          method: 'post',
          params: {
            unitId: this.selectedUnitId!,
            ...this.dialog.item,
          },
        });
        this.dialog.show = false;

        if (result) {
          this.dialog.item = structuredClone(initFieldDetailItem);
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: [
              'データの保存に失敗しました。下記内容を確認してください。',
            ],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    async select(selectedItem: Field) {
      if (!this.selectedUnitId) {
        return;
      }
      if (this.table.items?.some((item) => item.id === selectedItem.id)) {
        alert('この入力項目は既に追加されています。');
        return;
      }
      if (
        this.table.items?.some(
          (item) => item.property === selectedItem.property,
        )
      ) {
        alert('対象 "' + selectedItem.property + '" は既に使われています。');
        return;
      }
      try {
        const result = await this.$api<
          InsertUnitFieldResponse,
          InsertUnitFieldRequest
        >({
          path: '/unitField/create',
          method: 'post',
          params: {
            unitId: this.selectedUnitId!,
            fieldId: selectedItem.id!,
          },
        });
        this.dialog.show = false;

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: [
              'データの保存に失敗しました。下記内容を確認してください。',
            ],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    handleClickRow(item: UpdateFieldRequest) {
      this.$set(this.detailDialog, 'show', true);
      this.$set(this.detailDialog, 'item', structuredClone(item));
      this.$set(this.detailDialog, 'newMode', false);
    },

    // 保存※管理者のみ
    async save() {
      if (!this.selectedUnitId) {
        return;
      }
      try {
        const result = await this.$api<UpdateFieldResponse, UpdateFieldRequest>(
          {
            path: '/field/update',
            method: 'post',
            params: this.detailDialog.item as UpdateFieldRequest,
          },
        );
        this.detailDialog.show = false;

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: [
              'データの保存に失敗しました。下記内容を確認してください。',
            ],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    async remove() {
      if (!this.selectedUnitId) {
        return;
      }
      if (!confirm('本当に削除しますか？')) {
        return;
      }
      try {
        const result = await this.$api<
          DeleteUnitFieldResponse,
          DeleteUnitFieldRequest
        >({
          path: '/unitField/delete',
          method: 'post',
          params: {
            unitId: this.selectedUnitId!,
            fieldId: this.detailDialog.item.id!,
          },
        });
        this.detailDialog.show = false;

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: ['削除に失敗しました。下記内容を確認してください。'],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    async saveOrder() {
      if (!this.table.items) {
        return;
      }
      if (!this.selectedUnitId) {
        return;
      }
      const unitId = this.selectedUnitId!;
      try {
        const result = await this.$api<
          OrderUnitFieldResponse,
          OrderUnitFieldRequest
        >({
          path: '/unitField/order',
          method: 'post',
          params: {
            items: this.table.items.map((item, order) => ({
              unitId,
              fieldId: item.id!,
              order: order + 1,
            })),
          },
        });

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: ['順番の保存に失敗しました。下記内容を確認してください。'],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    ...mapActions({
      snackbarRegister: 'register',
    }),
  },
});
</script>

<style lang="scss" scoped></style>
